import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import WidgetCard from './WidgetCard';
import WidgetCatalog from './WidgetCatalog';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import DashboardFilterBar from './filters/DashboardFilterBar';
import { useDashboardFilters } from './filters/useDashboardFilters';
import { toApiFilters } from './filters/filter-key';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './widget-grid.css';

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
// md kept at 12 cols (not 10) to match lg. The default bento's top row is 4 KPI
// cards across at 12-col coords (the 4th spans cols 9–12); a 10-col md
// grid overflows that 4th card and RGL bumps it to the next row — visible as a
// misaligned/floating card on first paint while the container width settles
// through the md band. Only lg/md layouts are supplied, so aligning their col
// counts removes the transient reflow. sm/xs/xxs still reflow for small screens.
const COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };
const ROW_HEIGHT = 80;

/**
 * Widget Grid — CSS Grid-based bento layout with manual drag-to-reorder.
 *
 * Widgets occupy grid cells defined by `x, y, w, h` (grid coords, not pixels).
 * Layout is pure CSS Grid with auto-flow; widgets render in order and flow naturally.
 * Manual drag is added via state: when user drops, call `onLayoutChange` to update positions.
 * Layout persists to `/api/v3/user-dashboard` keyed by `layoutKey`.
 */
export default function WidgetGrid({ layoutKey = 'default' }) {
    const { t } = useTranslation();
    const { propertyAccess } = useAuth();
    const { filters: dashFilters, filtersReady } = useDashboardFilters();
    const [catalog, setCatalog] = useState([]);
    const [layout, setLayout] = useState([]);
    const [widgetMap, setWidgetMap] = useState({});
    const [showCatalog, setShowCatalog] = useState(false);
    const [justAdded, setJustAdded] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedHint, setSavedHint] = useState('');
    const [editMode, setEditMode] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [booting, setBooting] = useState(true);

    // Tell the sidebar to auto-collapse while the Add-Widget catalog is open
    // (it restores the user's previous state when the catalog closes).
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('v3-catalog-open', { detail: showCatalog }));
    }, [showCatalog]);

    // Load catalog + user's saved layout
    useEffect(() => {
        (async () => {
            setBooting(true);
            setLoadError(null);
            try {
                const cat = await v3Api.widgets.catalog();
                const widgets = cat?.data?.widgets || [];
                setCatalog(widgets);
                setWidgetMap(Object.fromEntries(widgets.map(w => [w.widget_id, w])));

                if (!widgets.length) {
                    setLoadError('No widgets returned by catalog endpoint. Seed may be missing or role filter is excluding you.');
                    setBooting(false);
                    return;
                }

                let nextLayout = null;
                try {
                    const saved = await v3Api.userDashboard.get(layoutKey);
                    if (saved?.data?.widgets?.length) {
                        const normalized = normalizeLayout(saved.data.widgets, widgets);
                        if (normalized.length) nextLayout = normalized;
                        else console.warn('[WidgetGrid] saved layout references unknown widget_ids; falling back to default');
                    }
                } catch { /* no saved layout yet */ }

                const initialLayout = nextLayout || buildDefaultLayout(widgets);
                setLayout(initialLayout);
            } catch (err) {
                console.error('[WidgetGrid] load failed:', err);
                setLoadError(err?.response?.data?.error || err?.message || 'Failed to load widgets');
            } finally {
                setBooting(false);
            }
        })();
    }, [layoutKey]);

    // Filters sent to widgets are derived from the dashboard filter context,
    // translated to the API's `{ property_id, dateRange }` shape. The backend
    // injects concessionaire_id from the auth session — never sent from here.
    const filters = useMemo(
        () => toApiFilters(dashFilters, propertyAccess),
        [dashFilters, propertyAccess]
    );

    const onSave = useCallback(async () => {
        setSaving(true);
        try {
            await v3Api.userDashboard.save({ layout_key: layoutKey, widgets: layout });
            setSavedHint(t('Saved ✓'));
            setTimeout(() => setSavedHint(''), 2000);
        } catch (err) {
            setSavedHint(`Failed: ${err.message}`);
        } finally {
            setSaving(false);
        }
    }, [layout, layoutKey]);

    const onAddWidget = useCallback((widget) => {
        // Each widget can only be added once per dashboard.
        if (layout.some(l => l.widget_id === widget.widget_id)) {
            setShowCatalog(false);
            return;
        }
        const w = widget.default_size?.w || 3;
        const h = widget.default_size?.h || 2;
        const id = `${widget.widget_id}-${Date.now()}`;
        const newItem = {
            widget_id: widget.widget_id,
            i: id,
            // Drop it at the TOP-LEFT so it lands in view (not appended off-screen
            // at the bottom). compactType:'vertical' pushes the rest down; it's
            // immediately draggable in edit mode so the user can reposition it.
            x: 0, y: 0,
            w, h,
            minW: 2, minH: 2,
            config: {},
        };
        setLayout(prev => [newItem, ...prev]);
        setJustAdded(id);
        window.setTimeout(() => setJustAdded(cur => (cur === id ? null : cur)), 1800);
        setShowCatalog(false);
        // Make sure we're in edit mode so the freshly-added widget can be dragged.
        setEditMode(true);
        // Scroll the grid back to the top so the new widget is visible.
        window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
    }, [layout]);

    const addedWidgetIds = useMemo(
        () => new Set(layout.map(l => l.widget_id)),
        [layout]
    );

    const onRemove = useCallback((i) => {
        setLayout(prev => prev.filter(item => item.i !== i));
    }, []);

    const onConfigChange = useCallback((i, nextConfig) => {
        setLayout(prev => prev.map(item => (item.i === i ? { ...item, config: nextConfig } : item)));
    }, []);

    const onResetLayout = useCallback(() => {
        if (window.confirm('Reset dashboard to default layout? Your current arrangement will be lost.')) {
            setLayout(buildDefaultLayout(catalog));
        }
    }, [catalog]);

    // react-grid-layout layouts: keyed by breakpoint, items use { i, x, y, w, h, minW, minH }
    const rglLayouts = useMemo(() => ({
        lg: layout.map(l => ({
            i: l.i,
            x: l.x, y: l.y, w: l.w, h: l.h,
            minW: l.minW || 2, minH: l.minH || 2,
            // Per-item lock. react-grid-layout@2.2.3 mounts once and ignores
            // isDraggable/isResizable prop changes, but it DOES re-read `layouts`
            // and checks `l.static` at drag/resize start — so this is what
            // actually enforces the Locked state.
            static: !editMode,
        })),
    }), [layout, editMode]);

    // When user drags / resizes, merge new x,y,w,h back into our layout state (keep widget_id + config)
    const onLayoutChange = useCallback((nextLg) => {
        if (!nextLg || !nextLg.length) return;
        setLayout(prev => {
            const byI = Object.fromEntries(nextLg.map(n => [n.i, n]));
            let changed = false;
            const merged = prev.map(item => {
                const n = byI[item.i];
                if (!n) return item;
                if (n.x === item.x && n.y === item.y && n.w === item.w && n.h === item.h) return item;
                changed = true;
                return { ...item, x: n.x, y: n.y, w: n.w, h: n.h };
            });
            return changed ? merged : prev;
        });
    }, []);

    return (
        <div className="v3 v3-widget-grid-shell" style={{ padding: '16px 24px' }}>
            {/* Header */}
            <div className="v3-widget-grid-header">
                <h1 className="v3-heading" style={{ margin: 0 }}>{t('Dashboard')}</h1>
                <div className="v3-widget-grid-actions">
                    <button
                        onClick={() => setEditMode(m => !m)}
                        className={`v3-btn ${editMode ? 'v3-btn-primary' : 'v3-btn-ghost'}`}
                        title={editMode ? t('Lock layout') : t('Edit layout')}
                    >
                        {editMode ? `🔓 ${t('Editing')}` : `🔒 ${t('Locked')}`}
                    </button>
                    {editMode && (
                        <>
                            <button onClick={() => setShowCatalog(true)} className="v3-btn v3-btn-primary">
                                + {t('Add Widget')}
                            </button>
                            <button onClick={onResetLayout} className="v3-btn v3-btn-ghost" title={t('Reset to default')}>
                                ↺ {t('Reset')}
                            </button>
                        </>
                    )}
                    <button onClick={onSave} disabled={saving} className="v3-btn v3-btn-ghost">
                        {saving ? t('Saving…') : savedHint || t('Save Layout')}
                    </button>
                </div>
            </div>

            {/* Filter bar — date range + property multi-select (Phase 1) */}
            <DashboardFilterBar />

            {/* Boot / error states */}
            {booting && (
                <div className="v3-card" style={{ padding: 32, textAlign: 'center', color: 'var(--v3-text-muted)' }}>
                    {t('Loading widgets…')}
                </div>
            )}
            {!booting && loadError && (
                <div className="v3-card" style={{ padding: 24, borderLeft: '3px solid var(--v3-red, #ef4444)' }}>
                    <div className="v3-label" style={{ color: 'var(--v3-red, #ef4444)', marginBottom: 6 }}>{t('Widgets unavailable')}</div>
                    <div className="v3-body">{loadError}</div>
                    <div className="v3-body" style={{ fontSize: 12, marginTop: 8, color: 'var(--v3-text-dim)' }}>
                        {t('Tip: check browser console & network tab, then refresh. If your session expired, re-login.')}
                    </div>
                </div>
            )}
            {!booting && !loadError && !layout.length && (
                <div className="v3-card" style={{ padding: 24, textAlign: 'center' }}>
                    <div className="v3-body">{t('Your dashboard is empty.')}</div>
                    <button onClick={() => setShowCatalog(true)} className="v3-btn v3-btn-primary" style={{ marginTop: 12 }}>
                        + {t('Add your first widget')}
                    </button>
                </div>
            )}

            {/* Draggable / resizable grid */}
            {!booting && !loadError && layout.length > 0 && (
                <SizedGrid
                    rglLayouts={rglLayouts}
                    editMode={editMode}
                    onLayoutChange={onLayoutChange}
                >
                    {layout.map(item => {
                        const widget = widgetMap[item.widget_id];
                        return (
                            <div key={item.i} className={`v3-widget-item${justAdded === item.i ? ' v3-just-added' : ''}`}>
                                {widget ? (
                                    <WidgetCard
                                        widget={widget}
                                        filters={filters}
                                        paused={!filtersReady}
                                        onRemove={() => onRemove(item.i)}
                                        config={item.config}
                                        onConfigChange={(cfg) => onConfigChange(item.i, cfg)}
                                    />
                                ) : (
                                    <div className="v3-card" style={{ padding: 16 }}>Unknown widget: {item.widget_id}</div>
                                )}
                            </div>
                        );
                    })}
                </SizedGrid>
            )}

            {showCatalog && (
                <WidgetCatalog
                    catalog={catalog}
                    addedIds={addedWidgetIds}
                    onAdd={onAddWidget}
                    onClose={() => setShowCatalog(false)}
                />
            )}
        </div>
    );
}

/* ────────────────────────── helpers ────────────────────────── */

function buildDefaultLayout(widgets) {
    const slots = [
        { id: 'total_sales_mtd',        pos: { x: 0, y: 0, w: 3, h: 2 }},
        { id: 'total_revenue',           pos: { x: 3, y: 0, w: 3, h: 2 }},
        { id: 'active_tenants',          pos: { x: 6, y: 0, w: 3, h: 2 }},
        // Footfall rather than conversion in the 4th slot: conversion is a ratio
        // of sales to footfall, so it goes blank whenever either feed is missing
        // or the two cover different dates — a poor first impression on a fresh
        // dashboard. Footfall is a direct count and stands on its own.
        { id: 'total_footfall',          pos: { x: 9, y: 0, w: 3, h: 2 }},
        { id: 'top_5_performers',        pos: { x: 0, y: 2, w: 6, h: 3 }},
        { id: 'ai_insights',             pos: { x: 6, y: 2, w: 6, h: 3 }},
        { id: 'category_distribution',   pos: { x: 0, y: 5, w: 4, h: 3 }},
        { id: 'predictive_growth',       pos: { x: 4, y: 5, w: 8, h: 3 }},
    ];
    return slots
        .filter(s => widgets.some(w => w.widget_id === s.id))
        .map(s => ({
            widget_id: s.id,
            i: `${s.id}-default`,
            x: s.pos.x, y: s.pos.y, w: s.pos.w, h: s.pos.h,
            minW: 2, minH: 2,
            config: {},
        }));
}

// Inner grid wrapper — measures its own width via the v2 hook (replaces removed WidthProvider HOC).
// Always rendered (no conditional mount) so ApexCharts inside widgets don't get torn off the DOM
// in a way React can't reconcile when width is first measured.
function SizedGrid({ rglLayouts, editMode, onLayoutChange, children }) {
    const { width: measured, containerRef } = useContainerWidth();
    // Until the container measures itself, render with a sensible default width so
    // ResponsiveGridLayout mounts once and only updates `width`, never re-mounts items.
    const width = measured > 0 ? measured : 1200;
    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            <ResponsiveGridLayout
                className={`v3-widget-layout ${editMode ? 'is-editing' : 'is-locked'}`}
                width={width}
                layouts={rglLayouts}
                breakpoints={BREAKPOINTS}
                cols={COLS}
                rowHeight={ROW_HEIGHT}
                margin={[16, 16]}
                containerPadding={[0, 0]}
                onLayoutChange={onLayoutChange}
                isDraggable={editMode}
                isResizable={editMode}
                draggableHandle=".drag-handle"
                compactType="vertical"
                preventCollision={false}
                useCSSTransforms
            >
                {children}
            </ResponsiveGridLayout>
        </div>
    );
}

// Clamp saved layouts into grid bounds and add min sizes for older records
function normalizeLayout(saved, widgets) {
    return saved
        .filter(item => widgets.some(w => w.widget_id === item.widget_id))
        .map(item => ({
            ...item,
            w: Math.max(2, Math.min(12, item.w || 3)),
            h: Math.max(2, item.h || 2),
            x: Math.max(0, Math.min(12 - (item.w || 3), item.x || 0)),
            y: Math.max(0, item.y || 0),
            minW: 2,
            minH: 2,
        }));
}
